import { useRecentCanvases, useSharedWithMeCanvases } from "@/api/canvas";
import {
  useRecentFileRecords,
  useSharedWithMeFileRecords,
  useWorkspaceFileRecords,
} from "@/api/fileRecords";
import {
  useRecentWorkspaceFlowSessions,
  useWorkspaceFlowSessions,
} from "@/api/flow_sessions";
import {
  useFavoriteCanvases,
  useFavoriteFileRecords,
} from "@/api/resourceTags";
import { useWorkspaceCanvases } from "@/api/workspaces";
import {
  RESOURCE_TAG_FAVORITE,
  type Canvas,
  type FileRecord,
  type InboxItem,
} from "@cohesive/api-client";
import { useMemo } from "react";
import {
  HOME_BROWSER_LIMIT,
  type BrowserItem,
  type HomeTab,
} from "../utils/homeBrowserTypes";

export interface HomeTabData {
  items: BrowserItem[];
  /** True while nothing is displayable yet (all active queries pending). */
  isPending: boolean;
  /**
   * True when any active query failed. On merged tabs a single failed source
   * would otherwise leave a gap that reads as an empty account, so surface the
   * error path (the list only shows it when there are no items to display).
   */
  isError: boolean;
  refetch: () => void;
}

const canvasItems = (canvases: Canvas[]): BrowserItem[] =>
  canvases.map((data): BrowserItem => ({ kind: "canvas", data }));

// No folder rows on home — folder navigation lives on the workspace detail
// page. (The recents endpoint already excludes folders; the favorites and
// per-workspace lists do not.)
const fileItems = (files: FileRecord[]): BrowserItem[] =>
  files
    .filter((record) => !record.path.endsWith("/"))
    .map((data): BrowserItem => ({ kind: "file", data }));

const isFavorite = (item: { tags?: string[] }): boolean =>
  !!item.tags?.includes(RESOURCE_TAG_FAVORITE);

/**
 * Data for the active home tab, optionally scoped to a single workspace.
 *
 * Unscoped (`workspaceId` undefined) each tab maps to a small set of org-wide
 * queries; scoped, it maps to the equivalent per-workspace queries:
 *
 * - Files    — workspace files the user created, newest first. Unscoped, the
 *              recents endpoint spans containers too, so it's filtered to
 *              workspace-class: a canvas or chat file belongs to its
 *              container, not to Home.
 * - Canvases — recent canvases.
 * - Chats    — recent chats.
 * - Starred  — favorited canvases + workspace files. Chats pin instead of
 *              starring, and skills/connectors sit favorites out. Scoped
 *              favorites tag-filter the workspace lists rather than the
 *              org-wide favorite queries, whose `workspace_id` is optional.
 *
 * Rules of hooks forbid conditional calls, so both query sets are always
 * declared and only `enabled` toggles between them. The workspace hooks
 * already self-gate on `!!workspaceId`, which composes with the tab gating.
 * Sources load independently and render progressively (a tab is "pending"
 * only while every one of its sources is still pending).
 */
export function useHomeBrowserData(
  tab: HomeTab,
  workspaceId?: string,
): HomeTabData {
  const isRecents = tab === "recents";
  const isCanvases = tab === "canvases";
  const isChats = tab === "chats";
  const isStarred = tab === "starred";
  const isShared = tab === "shared";

  const scoped = !!workspaceId;

  // ----- org-wide (unscoped home) -----
  const recentCanvases = useRecentCanvases(HOME_BROWSER_LIMIT, {
    enabled: !scoped && isCanvases,
  });
  const recentFiles = useRecentFileRecords(HOME_BROWSER_LIMIT, {
    enabled: !scoped && isRecents,
  });
  const recentChats = useRecentWorkspaceFlowSessions(HOME_BROWSER_LIMIT, {
    enabled: !scoped && isChats,
  });
  const favoriteCanvases = useFavoriteCanvases({
    enabled: !scoped && isStarred,
  });
  const favoriteFiles = useFavoriteFileRecords({
    enabled: !scoped && isStarred,
  });
  // Directly shared canvases and files surface here AND in the rail's
  // Shared with me — one tree, both surfaces. Home-only (the tab is
  // filtered off workspace detail strips).
  const sharedFiles = useSharedWithMeFileRecords({
    enabled: !scoped && isShared,
  });
  const sharedCanvases = useSharedWithMeCanvases({
    enabled: !scoped && isShared,
  });

  // ----- workspace-scoped. Canvases/files are also enabled on Starred,
  // because the scoped favorites are a tag-filter over these same lists.
  const wsCanvases = useWorkspaceCanvases(workspaceId, {
    enabled: scoped && (isCanvases || isStarred),
  });
  const wsFiles = useWorkspaceFileRecords(workspaceId, {
    enabled: scoped && (isRecents || isStarred),
  });
  const wsChats = useWorkspaceFlowSessions(workspaceId, {
    enabled: scoped && isChats,
  });

  // The query feeding each kind on the active tab.
  const canvasQuery = scoped ? wsCanvases : recentCanvases;
  const fileQuery = scoped ? wsFiles : recentFiles;
  const chatQuery = scoped ? wsChats : recentChats;

  const items = useMemo<BrowserItem[]>(() => {
    // Mirrors useWorkspaceBrowserData's guard: the scoped chat lists are
    // server-filtered, but a chat moved to another workspace lingers in this
    // cache until refetch — drop rows that no longer belong. (Strict on the
    // optional flow_session relation, same trade-off as the old detail page.)
    const chatItems = (chats: InboxItem[]): BrowserItem[] =>
      chats
        .filter((c) => !scoped || c.flow_session?.workspace_id === workspaceId)
        .map((data): BrowserItem => ({ kind: "chat", data }));

    switch (tab) {
      // The workspace page's unified tree tab; Home has no equivalent view,
      // and the workspace browser reads its rows from useWorkspaceBrowserData.
      case "library":
        return [];
      case "recents":
        // Unscoped, keep workspace files only — a container file belongs to
        // its canvas/chat, not to Home.
        return fileItems(
          scoped
            ? (fileQuery.data ?? [])
            : (fileQuery.data ?? []).filter(
                (record) => record.class === "workspace",
              ),
        );
      case "shared":
        return [
          ...canvasItems(sharedCanvases.data ?? []),
          ...fileItems(sharedFiles.data ?? []),
        ];
      case "canvases":
        return canvasItems(canvasQuery.data ?? []);
      case "chats":
        return chatItems(chatQuery.data ?? []);
      case "starred":
        // Canvases + workspace files only — chats pin instead of starring.
        return scoped
          ? [
              ...canvasItems((wsCanvases.data ?? []).filter(isFavorite)),
              ...fileItems((wsFiles.data ?? []).filter(isFavorite)),
            ]
          : [
              ...canvasItems(favoriteCanvases.data ?? []),
              ...fileItems(favoriteFiles.data ?? []),
            ];
    }
  }, [
    tab,
    scoped,
    workspaceId,
    canvasQuery.data,
    fileQuery.data,
    sharedFiles.data,
    sharedCanvases.data,
    chatQuery.data,
    wsCanvases.data,
    wsFiles.data,
    favoriteCanvases.data,
    favoriteFiles.data,
  ]);

  // The queries feeding the active tab, for pending/error/refetch aggregation.
  const activeQueries = isShared
    ? [sharedCanvases, sharedFiles]
    : isRecents
      ? [fileQuery]
      : isCanvases
        ? [canvasQuery]
        : isChats
          ? [chatQuery]
          : isStarred
            ? scoped
              ? [wsCanvases, wsFiles]
              : [favoriteCanvases, favoriteFiles]
            : [];

  return {
    items,
    // `every` on an empty set is true — a tab with no active sources (the
    // detail-only tabs rendered unscoped) is empty, not loading.
    isPending:
      activeQueries.length > 0 && activeQueries.every((q) => q.isPending),
    isError: activeQueries.some((q) => q.isError),
    refetch: () => activeQueries.forEach((q) => void q.refetch()),
  };
}
